import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const variantStyleProps = {
  default: { backgroundColor: '#1f2937', color: '#ffffff' },
  success: { backgroundColor: '#16a34a', color: '#ffffff' },
  error: { backgroundColor: '#dc2626', color: '#ffffff' },
  warning: { backgroundColor: '#f59e0b', color: '#000000' },
  info: { backgroundColor: '#2563eb', color: '#ffffff' }
};

const Tooltip = ({
  text,
  title,
  children,
  variant = 'default',
  placement = 'top',
  delay = 200
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });

  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }
  };

  const showTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    updatePosition();
    setIsMounted(true);
    timeoutRef.current = setTimeout(() => setIsVisible(true), delay || 10);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
    timeoutRef.current = setTimeout(() => setIsMounted(false), 200);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getTooltipPosition = () => {
    const offset = 10;
    let top = 0;
    let left = 0;
    let transform = 'translate(-50%, -100%)';

    switch (placement) {
      case 'top':
        top = coords.top - offset;
        left = coords.left + coords.width / 2;
        transform = 'translate(-50%, -100%)';
        break;
      case 'bottom':
        top = coords.top + coords.height + offset;
        left = coords.left + coords.width / 2;
        transform = 'translate(-50%, 0)';
        break;
      case 'left':
        top = coords.top + coords.height / 2;
        left = coords.left - offset;
        transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        top = coords.top + coords.height / 2;
        left = coords.left + coords.width + offset;
        transform = 'translate(0, -50%)';
        break;
      default:
        top = coords.top - offset;
        left = coords.left + coords.width / 2;
        transform = 'translate(-50%, -100%)';
    }

    return { top, left, transform };
  };

  const tooltipText = text ?? title ?? '';
  const position = getTooltipPosition();
  const style = {
    position: 'fixed',
    top: `${position.top}px`,
    left: `${position.left}px`,
    transform: position.transform,
    zIndex: 99999,
    padding: '8px 12px',
    borderRadius: '10px',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    opacity: isVisible ? 1 : 0,
    transition: 'opacity 200ms ease-out, transform 200ms ease-out',
    ...variantStyleProps[variant]
  };

  const arrowBaseStyle = {
    position: 'absolute',
    width: '10px',
    height: '10px',
    backgroundColor: variantStyleProps[variant].backgroundColor,
    transform: 'rotate(45deg)',
    zIndex: -1
  };

  const arrowPosition = {
    top: placement === 'bottom' ? '-5px' : placement === 'top' ? 'unset' : '50%',
    bottom: placement === 'top' ? '-5px' : 'unset',
    left:
      placement === 'top' || placement === 'bottom'
        ? '50%'
        : placement === 'right'
        ? '-5px'
        : 'unset',
    right: placement === 'left' ? '-5px' : 'unset',
    transform:
      placement === 'top'
        ? 'translateX(-50%) rotate(45deg)'
        : placement === 'bottom'
        ? 'translateX(-50%) rotate(45deg)'
        : placement === 'left'
        ? 'translateY(-50%) rotate(45deg)'
        : placement === 'right'
        ? 'translateY(-50%) rotate(45deg)'
        : 'translateX(-50%) rotate(45deg)'
  };

  return (
    <div
      ref={containerRef}
      style={{ display: 'inline-block' }}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}

      {isMounted &&
        createPortal(
          <div role="tooltip" style={style}>
            {tooltipText}
            <div style={{ ...arrowBaseStyle, ...arrowPosition }} />
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
